import { PureComponent, RefObject, KeyboardEvent } from 'react';

import { FormattedMessage, injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { Popup } from '@atlaskit/popup';

import { ScreenReadersOnly } from 'src/components/accessibility';

import RefCopyField from '../ref-copy-field/ref-copy-field';
import RefBranch from '../refs/ref-branch';

import { BranchInfo } from './branch-info';
import { messages } from './branch-info-popup.i18n';
import * as styles from './branch-info-popup.styled';

export type BranchInfoDialogProps = {
  /** A URL for the branch link. */
  branchHref?: string;
  /** The name of the branch. */
  branchName: string;
  /** A URL to load the repository avatar image from. Passed through to @atlaskit/avatar. */
  repoAvatarSrc?: string;
  /** A URL for the repository link. */
  repoHref?: string;
  /** The name of the repository. */
  repoName?: string;
  /** A URL to load the repository owner avatar image from. Passed through to @atlaskit/avatar. */
  repoOwnerAvatarSrc?: string;
  /** A URL for the repository owner link. */
  repoOwnerHref?: string;
  /** The name of the repository owner. */
  repoOwnerName?: string;
  /** Text to show inside dialog trigger, which may sometimes be different to branchName */
  description: string;
  tabIndex?: number;
  /** Helps distinguish if the pop-up should support retargeting of the branch. */
  supportsRetargeting?: boolean;
  intl: IntlShape;
};

type State = {
  isOpen: boolean;
};

class BranchInfoPopup extends PureComponent<BranchInfoDialogProps, State> {
  static defaultProps: Partial<BranchInfoDialogProps> = {};

  state = {
    isOpen: false,
  };

  getBranchName = (): string => {
    const { branchName } = this.props;
    return branchName;
  };

  handleClose = (): void => {
    this.setState({ isOpen: false });
  };

  handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === ' ' || e.key === 'Enter') {
      this.toggleDialog();
      e.preventDefault(); // prevent page scroll down
    }
  };

  toggleDialog = (): void => {
    this.setState(prevState => ({ isOpen: !prevState.isOpen }));
  };

  render(): JSX.Element {
    const {
      branchHref,
      branchName,
      repoOwnerAvatarSrc,
      repoOwnerHref,
      repoOwnerName,
      repoAvatarSrc,
      repoHref,
      repoName,
      description,
      tabIndex,
      supportsRetargeting,
      intl,
    } = this.props;

    const DialogContent = (
      <styles.BranchInfoPopupContent>
        <BranchInfo
          branchHref={branchHref}
          branchName={branchName}
          repoAvatarSrc={repoAvatarSrc}
          repoHref={repoHref}
          repoName={repoName}
          repoOwnerAvatarSrc={repoOwnerAvatarSrc}
          repoOwnerHref={repoOwnerHref}
          repoOwnerName={repoOwnerName}
          supportsRetargeting={supportsRetargeting}
          closeBranchInfo={this.handleClose}
        />
        <RefCopyField refName={this.getBranchName()} />
      </styles.BranchInfoPopupContent>
    );

    return (
      <Popup
        placement="bottom-start"
        content={() => DialogContent}
        isOpen={this.state.isOpen}
        onClose={this.handleClose}
        trigger={triggerProps => {
          const { ref } = triggerProps;
          return (
            <styles.PopupChildrenWrapper
              {...triggerProps}
              ref={ref as RefObject<HTMLDivElement>} // fix ref type error
              role="button"
              onClick={this.toggleDialog}
              onKeyDown={this.handleKeyDown}
              tabIndex={tabIndex || 0}
              aria-label={
                supportsRetargeting
                  ? intl.formatMessage(messages.changeDestinationBranchLabel)
                  : ''
              }
            >
              <ScreenReadersOnly>
                <FormattedMessage
                  {...messages.branchLabel}
                  values={{
                    branchName: description,
                  }}
                />
              </ScreenReadersOnly>
              <RefBranch ariaHidden name={description} />
            </styles.PopupChildrenWrapper>
          );
        }}
      />
    );
  }
}

// eslint-disable-next-line import/no-default-export
export default injectIntl(BranchInfoPopup);
